// ===== TENANT SLICE =====
// src/features/auth/state/tenant-state/tenantSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { csrfHeaders } from '@/lib/session';

// Types
interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  slug?: string; // Added for compatibility
  logo?: string;
  primaryColor?: string;
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
  features: string[];
  settings: TenantSettings;
  createdAt: string;
  updatedAt: string;
}

interface TenantSettings {
  allowSignup: boolean;
  requireEmailVerification: boolean;
  enableMFA: boolean;
  enableSSO: boolean;
  maxUsers: number;
  storageQuota: number;
  customDomain?: string;
}

interface TenantMember {
  id: string;
  userId: string;
  tenantId: string;
  role: 'owner' | 'admin' | 'member' | 'guest';
  permissions: string[];
  joinedAt: string;
}

interface TenantState {
  current: Tenant | null;
  available: Tenant[];
  members: TenantMember[];
  isLoading: boolean;
  error: string | null;
  isValidatingWorkspace: boolean;
}

interface CreateTenantData {
  name: string;
  subdomain: string;
  plan?: string;
}

// Initial state
const initialState: TenantState = {
  current: null,
  available: [],
  members: [],
  isLoading: false,
  error: null,
  isValidatingWorkspace: false,
};

// Async thunks
export const fetchTenantBySubdomainThunk = createAsyncThunk(
  'tenant/fetchBySubdomain',
  async (subdomain: string) => {
    const response = await fetch(`/api/tenants/subdomain/${subdomain}`);
    
    if (!response.ok) {
      throw new Error('Tenant not found');
    }
    
    const data = await response.json();
    return data;
  }
);

export const fetchUserTenantsThunk = createAsyncThunk(
  'tenant/fetchUserTenants',
  async (userId: string) => {
    const response = await fetch(`/api/users/${userId}/tenants`, {
      credentials: 'include',
      headers: {
        ...csrfHeaders(),
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch user tenants');
    }
    
    const data = await response.json();
    return data;
  }
);

export const switchTenantThunk = createAsyncThunk(
  'tenant/switch',
  async (tenantId: string) => {
    const response = await fetch('/api/auth/switch-tenant', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...csrfHeaders(),
      },
      body: JSON.stringify({ tenantId }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to switch tenant');
    }
    
    const data = await response.json();
    return data;
  }
);

export const createTenantThunk = createAsyncThunk(
  'tenant/create',
  async (tenantData: CreateTenantData) => {
    const response = await fetch('/api/tenants', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...csrfHeaders(),
      },
      body: JSON.stringify(tenantData),
    });
    
    if (!response.ok) {
      throw new Error('Failed to create tenant');
    }
    
    const data = await response.json();
    return data;
  }
);

export const validateWorkspaceThunk = createAsyncThunk(
  'tenant/validateWorkspace',
  async (subdomain: string) => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
    const response = await fetch(`${apiUrl}/api/v1/auth/workspace/${subdomain}/validate`);

    if (!response.ok) {
      throw new Error('Invalid workspace');
    }

    const data = await response.json();
    return data;
  }
);

// Slice
const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setCurrentTenant: (state, action: PayloadAction<Tenant>) => {
      state.current = action.payload;
    },
    clearCurrentTenant: (state) => {
      state.current = null;
    },
    updateTenantSettings: (state, action: PayloadAction<Partial<TenantSettings>>) => {
      if (state.current) {
        state.current.settings = {
          ...state.current.settings,
          ...action.payload,
        };
      }
    },
    setTenantError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearTenantError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch tenant by subdomain
    builder
      .addCase(fetchTenantBySubdomainThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTenantBySubdomainThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.current = action.payload;
      })
      .addCase(fetchTenantBySubdomainThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch tenant';
      });

    // Fetch user tenants
    builder
      .addCase(fetchUserTenantsThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserTenantsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.available = action.payload;
        
        // If current tenant is not set, set the first one
        if (!state.current && action.payload.length > 0) {
          state.current = action.payload[0];
        }
      })
      .addCase(fetchUserTenantsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch tenants';
      });

    // Switch tenant
    builder
      .addCase(switchTenantThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(switchTenantThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.current = action.payload.tenant;
        
        // Redirect to tenant subdomain if needed
        if (action.payload.redirect) {
          window.location.href = action.payload.redirectUrl;
        }
      })
      .addCase(switchTenantThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to switch tenant';
      });

    // Create tenant
    builder
      .addCase(createTenantThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTenantThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.current = action.payload;
        state.available.push(action.payload);
      })
      .addCase(createTenantThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create tenant';
      });

    // Validate workspace
    builder
      .addCase(validateWorkspaceThunk.pending, (state) => {
        state.isValidatingWorkspace = true;
      })
      .addCase(validateWorkspaceThunk.fulfilled, (state) => {
        state.isValidatingWorkspace = false;
      })
      .addCase(validateWorkspaceThunk.rejected, (state) => {
        state.isValidatingWorkspace = false;
      });
  },
});

// Actions
export const {
  setCurrentTenant,
  clearCurrentTenant,
  updateTenantSettings,
  setTenantError,
  clearTenantError,
} = tenantSlice.actions;

// Alias for compatibility
export const clearTenant = clearCurrentTenant;

// Selectors
export const selectTenant = (state: RootState) => state.tenant;
export const selectCurrentTenant = (state: RootState) => state.tenant.current;
export const selectAvailableTenants = (state: RootState) => state.tenant.available;
export const selectTenantLoading = (state: RootState) => state.tenant.isLoading;
export const selectTenantError = (state: RootState) => state.tenant.error;
export const selectIsValidatingWorkspace = (state: RootState) => state.tenant.isValidatingWorkspace;

// Export reducer
export const tenantReducer = tenantSlice.reducer;
export default tenantSlice.reducer;
